import "../styles/tailwind.css";
import "../styles/globals.scss";
import "../styles/theme.scss";
import "../styles/components/post.scss";
import "../styles/components/dat.gui.css";
import "swiper/css";
import "swiper/css/zoom";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "../styles/pages/product.scss";
import "../styles/pages/account-discounts.scss";
import "../styles/pages/account-notifications.scss";
import "../styles/pages/unsubscribed.scss";
import "../styles/components/listing.scss";
import "../styles/components/category-tag-pills.scss";
import "../styles/components/account.scss";
import "../styles/components/guide-edit-panel.scss";
import "../styles/components/earnings.scss";
import "../styles/components/wallet-payout.scss";
import "../styles/account-search-api.css";
import "../styles/components/price-slider.scss";
import "../styles/components/price-gauge.scss";
import "../styles/components/racoons-post-intro.scss";
import "../styles/components/profile.scss";
import "../styles/components/form-inputs.scss";
import "../styles/components/notifications.scss";
import "../styles/components/tooltip.scss";
import "../styles/components/account-buttons.scss";
import "../styles/components/account-nav-button.scss";
import "../styles/components/side-rail.scss";
import "../styles/components/account-column.scss";
import "../styles/components/account-settings.scss";
import "../styles/components/discount-modal.scss";
import "../styles/components/mobile-nav-stack.scss";
import "../styles/components/mobile-top-nav.scss";
import "../styles/components/top-header.scss";
import "../styles/components/smaller-header.scss";
import "../styles/components/account-header.scss";
import "../styles/components/nav.scss";
import "../styles/components/layout.scss";
import "../styles/components/flexbox.scss";
import "../styles/components/chat.scss";
import "../styles/components/flags.scss";
import "../styles/components/tabs.scss";
import "../styles/components/login.scss";
import "../styles/components/nav-category-bar.scss";
import "../styles/components/login-palms.scss";
import "../styles/components/toggle-switch.scss";
import "../styles/components/sub-menu.scss";
import "../styles/components/mobile-info-popup.scss";
import "../styles/components/checkout.scss";
import "../styles/components/faq.scss";
import "../styles/components/email-capture.scss";
import "../styles/components/seller-discounts.scss";
import "../styles/components/shipping-selector.scss";
import "../styles/components/learn-more.scss";
import "../styles/components/animated-checkmark.scss";
import "../styles/animations.scss";
import "../styles/components/footer.scss";
import "../styles/components/seller-storefront.scss";
import "../styles/components/drawer.scss";
import "../styles/components/address-drawer.scss";
import "../styles/swtv.scss";
import "../styles/sales-filters.scss";
import "../styles/components/image-editor.scss";
import "../styles/components/guides.scss";
import "../styles/components/tag-graph.scss";
import "../styles/components/smart-pricing-toggle.scss";
import "../styles/components/flip-switch.scss";
import "../styles/components/toggle-3d.scss";
import "../styles/pages/sell-wizard.scss";
import "../styles/pages/bedrock.scss";
import "../styles/components/filter-panel.scss";
import "../styles/components/filter-drawer.scss";
import "../styles/components/wishlist-button.scss";
import "../styles/components/search-view-toggle.scss";
import "../styles/components/search-sort-select.scss";

import { useEffect } from "react";
import { useRouter } from "next/router";
import { beginAdPageView } from "../components/ad-slot-budget";
import { resetAdInsertBatch } from "../components/ad-insert-batch";
import { installAdRefreshGuard } from "../components/ad-refresh-guard";
import { startTabCrashSentinel, resetCrashContext } from "../utils/tab-crash-sentinel";
import { installAdResizeRebuildGuard } from "../components/ad-resize-rebuild-guard";
import { CompositeAppProvider } from "../contexts";
import DashGate, { isDashPath } from "../components/dash/dash-gate";
import { BaseErrorBoundary } from "../components/error-boundaries";
import { consoleErrorInterceptor } from "../utils/console-error-interceptor";
import { createNotification } from "../utils/dom";
import { installNextDataFetchGuard } from "../utils/next-data-fetch-guard";
import { SessionProvider, useSession } from "next-auth/react"
import { useAnalytics } from "../hooks/useAnalytics"
import { useSessionTracking } from "../hooks/useSessionTracking"
import { useSellwildNativeBootstrap } from "../hooks/useSellwildNativeBootstrap"
import { installCapacitorApiPatch } from "../utils/native/api-base"
import { installScriptHealth } from "../utils/script-health"
import { installBackReplayFetch } from "../utils/back-replay-fetch"
import { installDashFetchCache } from "../utils/dash-fetch-cache"
import { installDomMutationGuard, DOM_SKIP_MESSAGE } from "../utils/dom-mutation-guard"
import { captureMessage, ErrorSeverity, trackError } from "../utils/error-tracking"
import {
  probeOwnOrigin,
  severityForOwnFetchFailure,
  shouldRetryTransportFailure,
  transportFailureContext,
} from "../utils/fetch-transport-failure"

// Patch fetch + sendBeacon to route /api/* to the live server when the
// bundle is loaded under capacitor://. Runs at module load (before
// SessionProvider's first /api/auth/session call).
if (typeof window !== "undefined") installCapacitorApiPatch();

// Which outside scripts this page got (an ad blocker can stop any of them).
// Listens from module load so it sees the scripts that load after it.
if (typeof window !== "undefined") installScriptHealth();

// Back from a listing answers the page's listing reads from what it showed,
// so the grid and rows come back at once instead of empty. After the
// Capacitor patch, so it sees the app's own relative /api URLs.
if (typeof window !== "undefined") installBackReplayFetch(window);

// A dash page opened before shows its data at once, refreshed behind it. Does
// nothing off /dash. See utils/dash-fetch-cache.ts.
if (typeof window !== "undefined") installDashFetchCache(window);

// Chrome's translation moves text nodes React owns, and React's next removal
// then throws and takes the whole page down to the error fallback. Installed
// before hydration. A skip on a translated page is info; one anywhere else may
// be our own bug, so it stays a warning.
if (typeof window !== "undefined") {
  installDomMutationGuard(window, (detail) =>
    captureMessage(DOM_SKIP_MESSAGE, detail.translated ? ErrorSeverity.INFO : ErrorSeverity.WARNING, {
      component: "dom-mutation-guard",
      extra: { ...detail },
    })
  );
}

const AUTH_REQUIRED_PREFIXES = ["/account"];

function AppContent({ Component, pageProps }) {
  const analytics = useAnalytics(); // Auto-track page views
  const { data: session, status } = useSession();
  const router = useRouter();
  useSessionTracking(); // Track session starts for logged-in users
  useSellwildNativeBootstrap();

  // Each page view gets its own ad-slot budget. Slots created by the previous
  // page stay registered with GAM (the widget has no disconnect handler, so
  // nothing destroys them) — until it grows one, this only bounds accumulation
  // within a page, which is where the infinite feeds are.
  //
  // Called during render, not from an effect: ads claim their slot inside a
  // state initializer, so a reset that ran after commit handed the next page's
  // first-wave ads an already-spent budget and they rendered nothing at all.
  beginAdPageView(router.asPath);

  // Client-side auth guard. Mobile/static builds have no SSR redirect for
  // /account/*, so the page boots and we bounce unauthenticated users to
  // /login here. On web with SSR, `status` is already "authenticated" by the
  // time the client hydrates, so this is a no-op.
  //
  // Held until the router is ready: a pre-rendered dynamic route such as
  // /account/my-listings/[id] reports that pattern as its asPath until then,
  // and login would send the seller back to a page called "[id]".
  useEffect(() => {
    if (status !== "unauthenticated") return;
    if (!router.isReady) return;
    const path = router.pathname;
    if (!AUTH_REQUIRED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return;
    const returnTo = encodeURIComponent(router.asPath);
    void router.replace(`/login?returnTo=${returnTo}`);
  }, [status, router, router.isReady, router.asPath]);

  // Keep the deployed widget from repainting ads that are already on screen
  // every time a later ad mounts. Document-scoped, not page-view scoped: the
  // GAM slots it protects outlive a client-side navigation. See
  // ../components/ad-refresh-guard — remove once the widget bundle is rebuilt.
  useEffect(() => {
    installAdRefreshGuard();
    // And keep it from rebuilding every ad on the page -- a fresh GAM slot each
    // time -- when a phone fires `resize` because its URL bar collapsed mid
    // scroll. See ../components/ad-resize-rebuild-guard.
    installAdResizeRebuildGuard();
  }, []);

  // The insertion batcher restarts on a client-side navigation: a new page is a
  // new page as far as ads are concerned, and a stale released-count would let
  // the next page's ads insert one at a time instead of together.
  //
  // Keyed on asPath, not pathname — `/product/[id]` -> `/product/[id]` is the
  // most common navigation on the site and leaves pathname unchanged.
  useEffect(() => {
    resetAdInsertBatch();
    // The crash heartbeat's counters (load-mores so far) are per page view too.
    resetCrashContext();
  }, [router.asPath]);

  // A tab the browser kills while open -- out of memory, on iOS above all --
  // runs nothing that could report it. This notices on the next load instead.
  // See ../utils/tab-crash-sentinel.
  useEffect(() => startTabCrashSentinel(), []);

  // Identify the user when the session has one; forget them only on a real sign-out.
  useEffect(() => {
    const userId = session?.user?.id;
    if (userId) {
      analytics?.identify(parseInt(userId));
      return undefined;
    }
    // "loading" is not signed out. Every full page load starts there, and
    // reset() then cleared a signed-in buyer's stored id and started a new
    // visit: on 2026-10-26 the owner's visit restarted on each return home.
    if (status !== 'unauthenticated') return undefined;
    // Nobody stored, nobody to forget. Without this every signed-out page load
    // asked the server again, and crawlers the firewall answers with an empty
    // 403 logged 86 "Unexpected end of JSON input" rows on 2026-10-27.
    if (!analytics?.hasUser()) return undefined;
    // A session re-read on window focus that fails also reads as signed out.
    // Ask once more before forgetting anyone; a failed check forgets nobody.
    let cancelled = false;
    let httpStatus: number | undefined;
    // 2026-10-27 18:04Z: one row after the known-users fix, a bare "Failed to
    // fetch" with no status -- the request was cut, not refused, and the row
    // could not say whether the page was leaving or our route was unreachable.
    // A cut on a live, online page gets one more attempt; a second cut asks
    // whether our origin answers at all.
    let retried = false;
    const startedAt = Date.now();
    const askSession = () => fetch('/api/auth/session');
    void askSession()
      .catch((firstCut: unknown) => {
        if (!shouldRetryTransportFailure(firstCut)) throw firstCut;
        retried = true;
        return askSession();
      })
      .then((res) => {
        httpStatus = res.status;
        if (!res.ok) throw new Error(`Session check answered HTTP ${res.status}`);
        return res.json();
      })
      .then((current: { user?: { id?: string } } | null) => {
        if (!cancelled && !current?.user?.id) analytics?.reset();
      })
      .catch(async (error: unknown) => {
        const transport = transportFailureContext(error);
        const elapsedMs = Date.now() - startedAt;
        const originReachable = retried && transport.transportFailure ? await probeOwnOrigin() : null;
        trackError(
          error instanceof Error ? error : new Error(String(error)),
          severityForOwnFetchFailure(error, ErrorSeverity.WARNING, transport),
          {
            component: '_app',
            action: 'confirm_sign_out',
            extra: { httpStatus: httpStatus ?? null, elapsedMs, retried, originReachable, ...transport },
          }
        );
      });
    return () => {
      cancelled = true;
    };
  }, [session, status, analytics]);

  // Install console.error interceptor for comprehensive error tracking
  // Note: window.onerror and unhandledrejection are already captured by ErrorTracker
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Install console.error interceptor
    consoleErrorInterceptor.install();

    return () => {
      consoleErrorInterceptor.uninstall();
    };
  }, []);

  // Vercel edge cache sometimes 500s on /_next/data/ JSON fetches. Next.js
  // treats this as an asset error and does a hard navigation (black screen).
  // Intercept these fetches: on failure, return a minimal valid response with
  // empty props so the page mounts and its own API fallback loads the data.
  // Every other fetch on the page -- including every vendor script's -- is
  // handed straight to the browser, so a cross-origin failure never picks up
  // one of our frames on its way back. See utils/next-data-fetch-guard.ts.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    return installNextDataFetchGuard(window, () => {
      createNotification({ type: 'error', msg: 'Failed to load page data. Please try again.', dismissed: false });
    });
  }, []);

  return (
    <BaseErrorBoundary level="page" isolate showDetails={false}>
      <CompositeAppProvider {...pageProps}>
        {/* The dash is admin-only; see components/dash/dash-gate.tsx. */}
        {isDashPath(router.pathname) ? (
          <DashGate>
            <Component {...pageProps} />
          </DashGate>
        ) : (
          <Component {...pageProps} />
        )}
      </CompositeAppProvider>
    </BaseErrorBoundary>
  );
}

function MyApp({ Component, pageProps: { session, ...pageProps } }) {
  return (
    <SessionProvider
      session={session}
      refetchInterval={0}
      // Re-read the session when the tab comes back. The server ends a session
      // whose account was closed, merged away or banned
      // (utils/session-account-standing.ts); without this re-read the page kept
      // looking signed in until a reload, and every action failed with a 401.
      refetchOnWindowFocus={true}
    >
      <AppContent Component={Component} pageProps={pageProps} />
    </SessionProvider>
  );
}

export default MyApp;
